import { useEffect, useRef, useState } from "react";
import { useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { slideReveal } from "./Autopilot.styled";
import { useLineTyping } from "hooks/useLineTyping/";
import { StoryboardSection } from "pages/media/types/";
import { parseScript } from "pages/media/helpers/scriptAnchors";
import { radius } from "@libertify/ui";

interface ScriptVisualProps {
  isActive: boolean;
  shortDescDone: boolean;
  sections: StoryboardSection[];
  autoScrollAllowed?: { readonly current: boolean };
}

const PREVIEW_COUNT = 2;

const sectionToLines = (
  section: StoryboardSection,
  index: number,
  t: (key: string, params?: Record<string, string>) => string
) => [
  t("media.autopilot.steps.script.chapterHeader", {
    number: String(index + 1).padStart(2, "0"),
    topic: section.topic,
  }),
  parseScript(section.script.content).text,
];

export const ScriptVisual: React.FC<ScriptVisualProps> = ({
  isActive,
  shortDescDone,
  sections,
  autoScrollAllowed,
}) => {
  const t = useTranslate();
  const [started, setStarted] = useState(false);
  if (isActive && shortDescDone && !started) {
    setStarted(true);
  }
  const shouldStart = started;
  const [expanded, setExpanded] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const previewLines = sections
    .slice(0, PREVIEW_COUNT)
    .flatMap((s, i) => sectionToLines(s, i, t));
  const moreLines = sections
    .slice(PREVIEW_COUNT)
    .flatMap((s, i) => sectionToLines(s, PREVIEW_COUNT + i, t));

  const { typed: previewTyped, allDone: previewDone } = useLineTyping(
    previewLines,
    shouldStart
  );
  const { typed: moreTyped } = useLineTyping(moreLines, expanded);

  const hasMore = sections.length > PREVIEW_COUNT;

  const typedLineCount = previewTyped.filter(Boolean).length;
  useEffect(() => {
    if (
      isActive &&
      typedLineCount > 0 &&
      autoScrollAllowed?.current !== false
    ) {
      bottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isActive, typedLineCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ScriptWrap>
      <ScriptArea>
        {previewLines.map((_, i) => (
          <ScriptLine key={i} $isHeader={i % 2 === 0}>
            {previewTyped[i] || "\u00A0"}
          </ScriptLine>
        ))}
        {expanded &&
          moreLines.map((_, i) => (
            <ScriptLine key={`more-${i}`} $isHeader={i % 2 === 0}>
              {moreTyped[i] || "\u00A0"}
            </ScriptLine>
          ))}
        <div ref={bottomRef} />
      </ScriptArea>
      {previewDone && hasMore && !expanded && (
        <ViewMoreLink onClick={() => setExpanded(true)}>
          {t("media.autopilot.steps.script.viewMore")}
        </ViewMoreLink>
      )}
    </ScriptWrap>
  );
};

const ScriptWrap = styled.div`
  padding: 14px 15px 12px;
  animation: ${slideReveal} 0.3s ease both;
`;

const ScriptArea = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #374151;
  line-height: 1.8;
  background: #fafafa;
  border-radius: ${radius.box}px;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const ScriptLine = styled.div<{ $isHeader: boolean }>`
  font-weight: ${({ $isHeader }) => ($isHeader ? 600 : 400)};
  color: ${({ $isHeader }) => ($isHeader ? "#111827" : "#6b7280")};
  margin-top: ${({ $isHeader }) => ($isHeader ? "10px" : "2px")};
  text-align: justify;
  &:first-child {
    margin-top: 0;
  }
`;

const ViewMoreLink = styled.span`
  font-size: 12px;
  color: #14b8a6;
  cursor: pointer;
  display: inline-block;
  margin-bottom: 4px;

  &:hover {
    color: #0d9488;
  }
`;
